import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { MapInfobox, MapOptions, MapSearchOptions, MapTheme } from '@bpd-library/types';
import {
    calculateMapDataBoundingBox,
    calculateMapDataCenter,
    environmentDetails,
    expandBoundingBox,
    functionIsTrue,
    getCountryBoundingBox,
} from '@bpd-library/utilities';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import mapboxgl from 'mapbox-gl';

import { Infobox } from '../infobox';
import { clearHover, MapState, mapStore, setMapInstance, setZoom } from './store';
import mapbox from './utilities/mapbox';

const MOBILE_TOGGLE_CLASS = 'map--mobile-toggle';
const MAP_HOVERING_CLASS = 'map--hovering';
const POINTER_FOCUS_CLASS = 'map__pointer--focus';

export interface MapProps extends Component {
    pointerExists: (id: string) => boolean;
}

const Map: FC<MapProps> = ({ element }) => {
    const DEFAULT_OPTIONS: MapOptions = {
        zoomButtons: true,
        mobileToggle: false,
        openInTab: false,
        autoOpenUrl: true,
        language: environmentDetails.languageIsoCode || 'nl',
        searchCountryIsoCodes: ['nl', 'de'],
    };

    let [options] = useProp<MapOptions>('options', {});
    const [theme] = useProp<MapTheme>('theme');
    const container = useElement<HTMLDivElement>('[js-hook-map-container]');
    const infoboxWrapper = useElement<HTMLDivElement>('[js-hook-infobox-wrapper]');

    let map: mapboxgl.Map;
    let mapboxHelpers: ReturnType<typeof mapbox>;

    useBindMethod('pointerExists', pointerExists);

    onDidLoad(async () => {
        if (options.mobileToggle) element.classList.add(MOBILE_TOGGLE_CLASS);
        options = { ...DEFAULT_OPTIONS, ...options };

        map = createMapboxInstance();
        mapboxHelpers = mapbox(map, options, theme);
        setMapInstance(map);

        await setupMapbox();

        subscribe();

        if (options.search)
            options.search.forEach((e: MapSearchOptions) => mapboxHelpers.search(e));
    });

    function subscribe() {
        const { data } = mapStore.getState();
        const { addData } = mapboxHelpers;

        addData(data);

        mapStore.subscribe(({ data }) => addData(data), ['data']);
        mapStore.subscribe(handleHover, ['hover']);
        mapStore.subscribe(handleClick, ['click']);
        mapStore.subscribe(handleFocus, ['focus']);
        mapStore.subscribe(handleIsochrone, ['isochroneSetting']);
        mapStore.subscribe(handleDirections, ['directionsSetting']);

        mapStore.subscribe(
            ({ layerRasterSettings }) =>
                layerRasterSettings && mapboxHelpers.raster(layerRasterSettings),
            ['layerRasterSettings'],
        );
    }

    function handleHover({ hover }: MapState) {
        const { current, previous } = hover;
        const canvasStyle = map.getCanvas().style;
        const { setFeatureState } = mapboxHelpers;

        /*
            Set hover state
        */
        if (hasHoverTheme()) {
            if (current) setFeatureState(current.variant, current.id, 'hover', true);
            if (previous) setFeatureState(previous.variant, previous.id, 'hover', false);
        }

        canvasStyle.cursor = current ? 'pointer' : '';
    }

    function handleClick({ click }: MapState) {
        if (!click) return;

        const { infobox } = click;

        clearHover();

        if (infobox) {
            showInfobox(infobox);
        } else {
            clearInfobox();
        }
    }

    function handleFocus({ focus }: MapState) {
        const { current, previous, options } = focus;
        const { setFeatureState, center } = mapboxHelpers;

        if (previous) {
            previous.forEach(({ id, variant }) => {
                if (hasHoverTheme()) setFeatureState(variant, id, 'focus', false);
                pointerFocusToggle(id, 'remove');
            });
        }

        if (current) {
            current.forEach((item) => {
                const { variant, id } = item;

                if (theme.polygon) {
                    if (hasHoverTheme()) setFeatureState(variant, id, 'focus', true);
                }
            });

            if (options?.zoom) {
                let bounds = calculateMapDataBoundingBox(current);
                bounds = expandBoundingBox(bounds, 0.05);

                map.fitBounds(bounds);
            } else if (!options?.static) {
                center([[calculateMapDataCenter(current)]]);
            }
        }
    }

    function handleIsochrone({ isochroneSetting }: MapState) {
        const { removeIsochrone, addIsochrone } = mapboxHelpers;

        if (isochroneSetting) {
            addIsochrone(isochroneSetting);
        } else {
            removeIsochrone();
        }
    }

    function handleDirections({ directionsSetting }: MapState) {
        const { addDirections, removeDirections } = mapboxHelpers;

        if (directionsSetting) {
            addDirections(directionsSetting);
        } else {
            removeDirections();
        }
    }

    function pointerFocusToggle(id: string, action: 'add' | 'remove') {
        const pointer = getPointerElementById(id);

        if (pointer) {
            pointer.classList[action](POINTER_FOCUS_CLASS);
            element.classList[action](MAP_HOVERING_CLASS);
        }
    }

    function showInfobox(infobox: MapInfobox) {
        clearInfobox();
        infoboxWrapper.current!.appendChild(
            <Infobox {...infobox} onClose={clearInfobox} openInTab={options.openInTab} />,
        );
    }

    function clearInfobox() {
        infoboxWrapper.current!.innerHTML = '';
    }

    function hasHoverTheme() {
        return theme.polygon && theme.polygon.settings[0].hover;
    }

    function createMapboxInstance() {
        mapboxgl.accessToken = environmentDetails.mapboxAccessToken || '';

        const mapboxOptions: mapboxgl.MapboxOptions = {
            container: container.current!,
            style: `mapbox://styles/${environmentDetails.mapboxStyle}`,
            minZoom: 4,
            maxZoom: 30,
            dragPan: true,
            scrollZoom: false,
        };

        if (options.center) mapboxOptions.center = options.center;
        if (options.zoom) mapboxOptions.zoom = options.zoom;
        if (options.boundingBox) mapboxOptions.bounds = options.boundingBox;

        if (!options.zoom && !options.center && !options.boundingBox) {
            mapboxOptions.bounds = getCountryBoundingBox(['nl']);
        }

        return new mapboxgl.Map(mapboxOptions);
    }

    function getMapboxLanguageString(): string {
        let language: string | undefined;

        // If language could be nl-NL etc., make sure to strip first two characters
        if (options.language) {
            language = options.language.substr(0, 2);
        }

        // nl is not supported, use multilanguage instead
        language = !language || language === 'nl' ? 'mul' : language;

        return language;
    }

    async function setupMapbox() {
        const { raster, geojson, layer, interaction, setupPointers } = mapboxHelpers;

        await functionIsTrue(() => map.isStyleLoaded());

        // Setup Mapbox Language
        const languageControl = new MapboxLanguage({
            defaultLanguage: getMapboxLanguageString(),
        });
        map.addControl(languageControl);

        // Setup Layer Raster
        // Layer raster can be set via the store and options attribute
        const optionsLayerRaster = options.layerRaster
            ? { layerRaster: options.layerRaster }
            : undefined;
        const storeLayerRaster = mapStore.getState().layerRasterSettings;

        if (optionsLayerRaster) {
            raster(optionsLayerRaster);
        } else if (storeLayerRaster) {
            raster(storeLayerRaster);
        }

        // Setup in case of polygons
        if (theme.polygon) {
            theme.polygon.settings.forEach((setting) => {
                geojson(setting);
                layer(setting);
                interaction(setting);
            });
        }

        // Setup in case of pointers
        if (theme.pointer) {
            setupPointers(element);
        }

        map.resize();
    }

    function getPointerElementById(id: string) {
        return element.querySelector(`#pointer-${id}`);
    }

    function pointerExists(id: string) {
        return !!getPointerElementById(id);
    }

    return (
        <Fragment>
            <div class="c-map">
                <div className="map__container" js-hook-map-container>
                    {options.zoomButtons && (
                        <div class="map__navigation">
                            <button
                                class="map__button"
                                onClick={() => {
                                    map.zoomIn();
                                    setZoom(map.getZoom());
                                }}>
                                <bpd-svg name="icons-plus"></bpd-svg>
                            </button>
                            <button
                                class="map__button"
                                onClick={() => {
                                    map.zoomOut();
                                    setZoom(map.getZoom());
                                }}>
                                <span class="map__minus"></span>
                            </button>
                        </div>
                    )}

                    <div class="map__infobox-wrapper" js-hook-infobox-wrapper></div>
                </div>
            </div>
        </Fragment>
    );
};

Map.props = {
    options: {
        type: Object,
    },
    theme: {
        type: Object,
        required: true,
    },
    zoomButtons: {
        type: Boolean,
    },
};

defineElement('bpd-map', Map);
